import type { ReactNode } from "react";

import { Icon, IconName } from "../../components/Icon";

// Shared frame for the footer's Company pages: a hero, then tabs between the
// four pages. Each page is its own route (src/routes.ts) and prerendered.

const COMPANY_PAGES: { path: string; label: string; icon: IconName }[] = [
  { path: "/about", label: "About & contact", icon: "people" },
  { path: "/support", label: "Email support", icon: "mail" },
  { path: "/contact", label: "Phone & office", icon: "call" },
  { path: "/whatsapp", label: "WhatsApp", icon: "chat" },
];

type Props = {
  path: string;
  eyebrow: string;
  title: ReactNode;
  intro: ReactNode;
  actions?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
};

export function CompanyLayout({
  path,
  eyebrow,
  title,
  intro,
  actions,
  aside,
  children,
}: Props) {
  return (
    <>
      <div className="container info-hero">
        <div className="info-hero__copy">
          <p className="eyebrow">{eyebrow}</p>
          <h1>{title}</h1>
          <p className="info-hero__intro">{intro}</p>
          {actions && <div className="info-hero__actions">{actions}</div>}
        </div>
        {aside && <div className="info-hero__aside">{aside}</div>}
      </div>
      <div className="container">
        <nav className="info-tabs" aria-label="Company pages">
          {COMPANY_PAGES.map((p) => (
            <a
              key={p.path}
              href={p.path}
              aria-current={p.path === path ? "page" : undefined}
            >
              <Icon name={p.icon} size={17} />
              {p.label}
            </a>
          ))}
        </nav>
      </div>
      <div className="container info-body">{children}</div>
    </>
  );
}

// A titled block inside a Company page.
export function InfoSection({
  title,
  lede,
  children,
}: {
  title: string;
  lede?: ReactNode;
  children: ReactNode;
}) {
  const id = title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return (
    <section className="info-section" aria-labelledby={id}>
      <div className="info-section__head">
        <h2 id={id}>{title}</h2>
        {lede && <p>{lede}</p>}
      </div>
      <div className="info-section__body">{children}</div>
    </section>
  );
}

export function Tile({
  icon,
  title,
  children,
  href,
  cta,
}: {
  icon: IconName;
  title: string;
  children: ReactNode;
  href?: string;
  cta?: string;
}) {
  const inner = (
    <>
      <span className="tile__icon">
        <Icon name={icon} size={22} color="var(--accent)" />
      </span>
      <h3>{title}</h3>
      <div className="tile__text">{children}</div>
      {cta && (
        <span className="tile__cta">
          {cta}
          <Icon name="arrow" size={16} />
        </span>
      )}
    </>
  );
  return href ? (
    <a className="tile tile--link" href={href}>
      {inner}
    </a>
  ) : (
    <div className="tile">{inner}</div>
  );
}

// "We will never ask for…" — repeated on every contact page on purpose.
export function SafetyNote() {
  return (
    <aside className="safety-note">
      <Icon name="shield" size={22} />
      <p>
        <strong>
          We will never ask for your PIN, your full card number or a bank OTP
        </strong>{" "}
        — not by email, phone or WhatsApp. Anyone who does isn't Hethera. Your
        PIN is only ever typed into the secure form inside WhatsApp. We only
        take payment by card, so we will never ask you to transfer money to an
        account number either.
      </p>
    </aside>
  );
}
